import React, { useState, useEffect, useRef } from 'react';
import '../css/admin/DeliveryPartnerManagement.css';
import { config } from '../utils/config';
import { FiPhone, FiX, FiSearch, FiShoppingBag } from 'react-icons/fi';
import { MdOutlineTwoWheeler } from 'react-icons/md';
import { message } from 'antd';

const DeliveryPartnerManagement = ({ onViewDetails }) => {
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [fleetIdMap, setFleetIdMap] = useState({}); // provider_id -> delivery_partner_id
  const fleetIdMapRef = useRef({}); // ref for immediate access without stale closure
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerDetails, setPartnerDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'online' | 'offline'
  const [kpiMetrics, setKpiMetrics] = useState({
    total_partners: 0,
    average_rating: 0,
    top_assigned_partner: 'None',
    top_assigned_partner_rating: 0
  });

  useEffect(() => {
    fetchDeliveryPartners();
  }, []);

  const fetchDeliveryPartners = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch delivery partners exclusively from delivery-fleet API
      const response = await fetch(
        `${config.API_BASE_URL}/kirazee/api/v1/admin/delivery-fleet/?page=1&limit=50`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch delivery partners`);
      }

      const data = await response.json();
      const partnersList = data.partners || data.providers || [];
      setDeliveryPartners(partnersList);

      setKpiMetrics({
        total_partners: data.pagination?.total_providers || partnersList.length,
        average_rating: Number(data.average_rating ?? 0),
        top_assigned_partner: data.top_assigned_partner || 'None',
        top_assigned_partner_rating: Number(data.top_assigned_partner_rating ?? 0)
      });

      // Build provider_id -> delivery_partner_id map directly from fleet response
      const map = {};
      partnersList.forEach(p => {
        if (p.provider_id && p.delivery_partner_id) {
          map[p.provider_id] = p.delivery_partner_id;
        }
      });
      fleetIdMapRef.current = map;
      setFleetIdMap(map);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerDetails = async (deliveryPartnerId) => {
    try {
      setDetailsLoading(true);
      const response = await fetch(
        `${config.API_BASE_URL}/kirazee/api/v1/admin/delivery-partners/${deliveryPartnerId}/full-details`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}: Failed to fetch partner details`);
      const json = await response.json();
      // API returns { success, data: { ...partner } }
      setPartnerDetails(json.success ? json.data : null);
    } catch (err) {
      console.error('Error fetching partner details:', err);
      setPartnerDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleEditToggle = () => {
    ``
    if (!isEditing) {
      setPendingUploads({});
      const currentStatus = (partnerDetails.status === 0 || partnerDetails.status === '0' || partnerDetails.status === 'Inactive' || partnerDetails.status === 'inactive' || partnerDetails.status === false) ? 0 : 1;
      setEditFormData({
        name: partnerDetails.name || '',
        email: partnerDetails.email || '',
        phone_number: partnerDetails.phone_number || '',
        status: currentStatus,
        vehicle_type: partnerDetails.vehicle_type || '',
        vehicle_number: partnerDetails.vehicle_number || '',
        financials: {
          pan_number: partnerDetails.financials?.pan_number || '',
          bank_account_number: partnerDetails.financials?.bank_account_number || '',
          ifsc_code: partnerDetails.financials?.ifsc_code || ''
        },
        documents: partnerDetails.documents ? partnerDetails.documents.map(doc => ({
          document_type: doc.document_type,
          is_verified: doc.is_verified
        })) : []
      });
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (field, value, isFinancial = false) => {
    if (field === 'phone_number') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }
    setEditFormData(prev => {
      if (isFinancial) {
        return {
          ...prev,
          financials: {
            ...prev.financials,
            [field]: value
          }
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleDocumentChange = (docType, isVerified) => {
    setEditFormData(prev => ({
      ...prev,
      documents: (prev.documents || []).map(doc =>
        doc.document_type === docType ? { ...doc, is_verified: isVerified } : doc
      )
    }));
  };

  const [pendingUploads, setPendingUploads] = useState({});

  const handleDocumentSelect = (docType, file) => {
    if (!file) return;
    setPendingUploads(prev => ({ ...prev, [docType]: file }));
  };

  const handleSaveDetails = async () => {
    if (editFormData.phone_number && editFormData.phone_number.length !== 10) {
      message.error("Phone number must be exactly 10 digits");
      return;
    }

    try {
      setIsSaving(true);
      const deliveryPartnerId = fleetIdMapRef.current[selectedPartner.provider_id];

      // 1. Upload any pending documents first
      const uploadPromises = Object.entries(pendingUploads).map(async ([docType, file]) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', docType);

        const response = await fetch(
          `${config.API_BASE_URL}/kirazee/delivery-partner/api/v1/partners/documents/upload/?partner_id=${deliveryPartnerId}`,
          {
            method: 'POST',
            body: formData
          }
        );

        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.message || `HTTP ${response.status}`);
        return json;
      });

      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
      }

      // 2. Update the rest of the details via PATCH
      const response = await fetch(
        `${config.API_BASE_URL}/kirazee/api/v1/admin/delivery-partners/${deliveryPartnerId}/full-details`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editFormData)
        }
      );

      const json = await response.json();
      if (!response.ok || !json.success) throw new Error(json.message || `HTTP ${response.status}`);

      message.success('Delivery partner details updated successfully');
      setShowDetailsModal(false);
      setPartnerDetails(null);
      setIsEditing(false);
      setPendingUploads({});
      fetchDeliveryPartners();
    } catch (err) {
      console.error('Error saving partner details:', err);
      message.error(err.message || 'Failed to update partner details');
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewDetails = (partner) => {
    setSelectedPartner(partner);
    setShowDetailsModal(true);
    // Use ref for immediate access — state may not be updated yet
    const deliveryPartnerId = fleetIdMapRef.current[partner.provider_id];
    if (!deliveryPartnerId) {
      console.error(`No delivery_partner_id found for provider_id ${partner.provider_id}. Map:`, fleetIdMapRef.current);
    }
    fetchPartnerDetails(deliveryPartnerId || partner.provider_id);
  };

  const handleViewOrders = (partner) => {
    const providerId = partner.provider_id || partner.user_id;
    if (onViewDetails) {
      onViewDetails(providerId, partner);
    } else {
      window.location.hash = 'delivery-details';
    }
  };

  const isActivePartner = (p) => {
    if (p.status === 0 || p.status === '0' || p.status_code === 0 || p.status === 'Inactive' || p.status === 'inactive') {
      return false;
    }
    return true;
  };

  const isPastPartner = (p) => !isActivePartner(p);

  const checkIsAvailable = (p) => {
    if (p.is_available === true || p.is_available === 1 || p.is_available === '1') {
      return true;
    }
    if (p.is_available === false || p.is_available === 0 || p.is_available === '0') {
      return false;
    }
    const s = String(p.status || '').toLowerCase().trim();
    return s === 'available' || s === 'online' || s === 'busy';
  };

  const isOnlinePartner = (p) => {
    if (!isActivePartner(p)) return false;
    return checkIsAvailable(p);
  };

  const isOfflinePartner = (p) => {
    if (!isActivePartner(p)) return false;
    return !checkIsAvailable(p);
  };

  const handleToggleStatus = async (partner, e) => {
    e.stopPropagation();
    const currentIsAvailable = checkIsAvailable(partner);
    const newIsAvailable = !currentIsAvailable;
    const deliveryPartnerId = fleetIdMapRef.current[partner.provider_id] || partner.delivery_partner_id || partner.provider_id;

    // Optimistic UI update for availability & status display
    setDeliveryPartners(prev =>
      prev.map(p =>
        p.provider_id === partner.provider_id
          ? {
            ...p,
            is_available: newIsAvailable
          }
          : p
      )
    );

    try {
      const response = await fetch(
        `${config.API_BASE_URL}/kirazee/api/v1/admin/delivery-partners/${deliveryPartnerId}/full-details`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_available: newIsAvailable })
        }
      );
      const json = await response.json();
      if (!response.ok || !json.success) {
        throw new Error(json.message || `HTTP ${response.status}`);
      }
      message.success(`Partner availability updated to ${newIsAvailable ? 'Online' : 'Offline'}`);
    } catch (err) {
      console.error('Error updating partner availability:', err);
      message.error(err.message || 'Failed to update partner availability');
      // Revert on error
      setDeliveryPartners(prev =>
        prev.map(p => (p.provider_id === partner.provider_id ? partner : p))
      );
    }
  };

  const totalCount = deliveryPartners.length;
  const pastCount = deliveryPartners.filter(isPastPartner).length;
  const activeCount = deliveryPartners.filter(isActivePartner).length;
  const onlineCount = deliveryPartners.filter(isOnlinePartner).length;
  const offlineCount = deliveryPartners.filter(isOfflinePartner).length;

  const filteredPartners = deliveryPartners.filter(partner => {
    const active = isActivePartner(partner);
    const past = isPastPartner(partner);
    const isOnline = checkIsAvailable(partner);

    // All tab: status = 1 (active partners only)
    if (statusFilter === 'all' && !active) return false;
    // Online tab: status = 1 & is_available = true
    if (statusFilter === 'online' && (!active || !isOnline)) return false;
    // Offline tab: status = 1 & is_available = false
    if (statusFilter === 'offline' && (!active || isOnline)) return false;
    // Past partners tab: status = 0 (past partners only)
    if (statusFilter === 'past' && !past) return false;

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      const nameMatch = (partner.name || '').toLowerCase().includes(q);
      const phoneMatch = (partner.phone || '').toLowerCase().includes(q);
      const idMatch = String(partner.provider_id || '').toLowerCase().includes(q);
      const vehicleMatch = (partner.vehicle_type || '').toLowerCase().includes(q);
      return nameMatch || phoneMatch || idMatch || vehicleMatch;
    }

    return true;
  });

  const getPartnerStatusText = (partner) => {
    if (isPastPartner(partner)) return 'Past Partner';
    const isOnline = checkIsAvailable(partner);
    if (!isOnline) return 'Offline';
    const s = String(partner.status || '').toLowerCase().trim();
    if (s === 'busy') return 'Busy';
    return 'Online';
  };

  const getStatusStyle = (partner) => {
    const text = getPartnerStatusText(partner);
    if (text === 'Online') {
      return { color: '#16a34a', background: '#dcfce7', borderColor: '#16a34a' };
    }
    if (text === 'Busy') {
      return { color: '#d97706', background: '#fef3c7', borderColor: '#d97706' };
    }
    if (text === 'Past Partner') {
      return { color: '#dc2626', background: '#fee2e2', borderColor: '#fca5a5' };
    }
    return { color: '#6b7280', background: '#f3f4f6', borderColor: '#d1d5db' };
  };

  const truncateName = (name, max = 16) =>
    name && name.length > max ? name.slice(0, max) + '...' : (name || 'Unknown');

  if (loading) {
    return (
      <div className="dpm-container">
        <div className="dpm-loading">
          <div className="dpm-spinner" />
          <p>Loading Delivery Partners...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dpm-container">
        <div className="dpm-error">
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button onClick={fetchDeliveryPartners} className="dpm-retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dpm-container">
      {/* Header */}
      <div className="dpm-header">
        <h1>Delivery Partners</h1>
        <span className="dpm-count">
          {filteredPartners.length} of {statusFilter === 'past' ? pastCount : activeCount} Partners
        </span>
      </div>

      {/* Fleet Performance Metrics Dashboard */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(42, 44, 65, 0.04)'
      }}>
        <h3 style={{
          margin: '0 0 16px 0',
          fontSize: '18px',
          fontWeight: '600',
          color: '#2A2C41'
        }}>
          Fleet Performance Metrics
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px'
        }}>
          <div style={{
            padding: '16px',
            background: 'white',
            border: '1px solid #2A2C41',
            borderRadius: '10px',
            transition: 'all 0.2s ease'
          }}>
            <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Total Partners</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#2A2C41' }}>{kpiMetrics.total_partners || 0}</div>
          </div>
          <div style={{
            padding: '16px',
            background: 'white',
            border: '1px solid #2A2C41',
            borderRadius: '10px',
            transition: 'all 0.2s ease'
          }}>
            <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Average Rating</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#2A2C41' }}>⭐ {kpiMetrics.average_rating?.toFixed(1) || '0.0'}</div>
          </div>
          <div style={{
            padding: '16px',
            background: 'white',
            border: '1px solid #2A2C41',
            borderRadius: '10px',
            transition: 'all 0.2s ease'
          }}>
            <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Top Assigned Partner</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#2A2C41', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {kpiMetrics.top_assigned_partner || 'None'}
              </span>
              <span style={{ fontSize: '14px', color: '#F59E0B', marginLeft: '8px', flexShrink: 0 }}>
                ⭐ {kpiMetrics.top_assigned_partner_rating?.toFixed(1) || '0.0'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Controls Bar: Filters & Search */}
      <div className="dpm-controls-bar">
        <div className="dpm-filter-tabs">
          <button
            className={`dpm-tab ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All <span className="dpm-tab-badge">{activeCount}</span>
          </button>
          <button
            className={`dpm-tab ${statusFilter === 'online' ? 'active' : ''}`}
            onClick={() => setStatusFilter('online')}
          >
            Online <span className="dpm-tab-badge online">{onlineCount}</span>
          </button>
          <button
            className={`dpm-tab ${statusFilter === 'offline' ? 'active' : ''}`}
            onClick={() => setStatusFilter('offline')}
          >
            Offline <span className="dpm-tab-badge offline">{offlineCount}</span>
          </button>
          <button
            className={`dpm-tab ${statusFilter === 'past' ? 'active' : ''}`}
            onClick={() => setStatusFilter('past')}
          >
            Past / Inactive <span className="dpm-tab-badge past">{pastCount}</span>
          </button>
        </div>

        <div className="dpm-search-wrapper">
          <FiSearch className="dpm-search-icon" size={16} />
          <input
            type="text"
            className="dpm-search-input"
            placeholder="Search by name, phone, ID, or vehicle..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="dpm-search-clear" onClick={() => setSearchTerm('')}>
              <FiX size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Cards Grid */}
      <div className="dpm-grid">
        {filteredPartners.length === 0 ? (
          <div className="dpm-empty">No delivery partners match your filters.</div>
        ) : (
          filteredPartners.map(partner => (
            <div key={partner.provider_id} className="dpm-card">
              {/* Card Top Row: Name + ID + Status badge + Active Toggle */}
              <div className="dpm-card-top">
                <div className="dpm-name-row">
                  <span className="dpm-name" title={partner.name}>
                    {truncateName(partner.name)}
                  </span>
                  <span className="dpm-id">ID: {partner.provider_id}</span>
                </div>
                <div className="dpm-status-toggle-wrapper">
                  <span
                    className="dpm-status-badge"
                    style={getStatusStyle(partner)}
                  >
                    • {getPartnerStatusText(partner)}
                  </span>
                  {!isPastPartner(partner) && (
                    <label className="dpm-toggle-switch" title={checkIsAvailable(partner) ? 'Set Offline' : 'Set Online'}>
                      <input
                        type="checkbox"
                        checked={checkIsAvailable(partner)}
                        onChange={(e) => handleToggleStatus(partner, e)}
                      />
                      <span className="dpm-toggle-slider" />
                    </label>
                  )}
                </div>
              </div>

              {/* Phone + Vehicle */}
              <div className="dpm-info-row">
                <span className="dpm-info-item">
                  <FiPhone size={12} /> {partner.phone || 'N/A'}
                </span>
                <span className="dpm-info-item">
                  <MdOutlineTwoWheeler size={14} /> {partner.vehicle_type || 'N/A'}
                </span>
              </div>

              {/* Stats */}
              <div className="dpm-stats">
                <div className="dpm-stat">
                  <span className="dpm-stat-value">{partner.completed_deliveries ?? 0}</span>
                  <span className="dpm-stat-label">COMPLETED</span>
                </div>
                <div className="dpm-stat">
                  <span className="dpm-stat-value">{partner.total_deliveries ?? 0}</span>
                  <span className="dpm-stat-label">TOTAL</span>
                </div>
                <div className="dpm-stat">
                  <span className="dpm-stat-value">
                    {typeof partner.total_kilometers_traveled === 'number'
                      ? partner.total_kilometers_traveled.toFixed(1)
                      : '0.0'}
                  </span>
                  <span className="dpm-stat-label">KM</span>
                </div>
              </div>

              {/* Card Actions: View Details & View Orders */}
              <div className="dpm-card-actions">
                <button className="dpm-view-btn" onClick={() => handleViewDetails(partner)}>
                  View Details
                </button>
                <button className="dpm-orders-btn" onClick={() => handleViewOrders(partner)}>
                  <FiShoppingBag size={14} style={{ marginRight: '4px' }} /> View Orders
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedPartner && (
        <div className="dpm-modal-overlay" onClick={() => { setShowDetailsModal(false); setPartnerDetails(null); setIsEditing(false); }}>
          <div className="dpm-modal" onClick={e => e.stopPropagation()}>
            <div className="dpm-modal-header">
              <div>
                <h2>{selectedPartner.name}</h2>
              </div>
              <button className="dpm-modal-close" onClick={() => { setShowDetailsModal(false); setPartnerDetails(null); setIsEditing(false); }}>
                <FiX size={20} />
              </button>
            </div>

            <div className="dpm-modal-body">
              {detailsLoading ? (
                <div className="dpm-modal-loading">
                  <div className="dpm-spinner" />
                  <p>Loading details...</p>
                </div>
              ) : partnerDetails ? (
                <>
                  {/* Basic Info */}
                  <div className="dpm-modal-section">
                    <h3>Basic Information</h3>
                    <div className="dpm-modal-grid">
                      {[
                        ['Name', partnerDetails.name, 'name'],
                        ['Email', partnerDetails.email, 'email'],
                        ['Phone', partnerDetails.phone_number, 'phone_number'],
                        ['Status', (partnerDetails.status === 0 || partnerDetails.status === '0' || partnerDetails.status === 'Inactive' || partnerDetails.status === 'inactive' || partnerDetails.status === false) ? 'Inactive' : 'Active', 'status'],
                        ['Vehicle Type', partnerDetails.vehicle_type, 'vehicle_type'],
                        ['Vehicle Number', partnerDetails.vehicle_number || 'N/A', 'vehicle_number'],
                        ['Rating', typeof partnerDetails.rating === 'number' ? partnerDetails.rating.toFixed(2) : 'N/A', null],
                        ['Available', partnerDetails.is_available ? 'Yes' : 'No', null],
                        ['Verified', partnerDetails.is_verified ? 'Yes' : 'No', null],
                        ['Total Deliveries', partnerDetails.total_deliveries ?? 0, null],
                        ['Registered', new Date(partnerDetails.created_at).toLocaleDateString(), null],
                      ].map(([label, value, fieldKey]) => (
                        <div key={label} className="dpm-modal-item">
                          <span className="dpm-modal-label">{label}</span>
                          {isEditing && fieldKey ? (
                            fieldKey === 'status' ? (
                              <select
                                value={editFormData.status ?? 1}
                                onChange={(e) => handleInputChange('status', Number(e.target.value))}
                                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box', marginTop: '4px', background: 'white' }}
                              >
                                <option value={1}>Active</option>
                                <option value={0}>Inactive</option>
                              </select>
                            ) : (
                              <input
                                type="text"
                                value={editFormData[fieldKey] || ''}
                                onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box', marginTop: '4px' }}
                              />
                            )
                          ) : (
                            <span className="dpm-modal-value">{value ?? 'N/A'}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Financial Details */}
                  {partnerDetails.financials && (
                    <div className="dpm-modal-section">
                      <h3>Financial Details</h3>
                      <div className="dpm-modal-grid">
                        {[
                          ['PAN Number', partnerDetails.financials.pan_number, 'pan_number'],
                          ['Bank Account', partnerDetails.financials.bank_account_number, 'bank_account_number'],
                          ['IFSC Code', partnerDetails.financials.ifsc_code, 'ifsc_code'],
                        ].map(([label, value, fieldKey]) => (
                          <div key={label} className="dpm-modal-item">
                            <span className="dpm-modal-label">{label}</span>
                            {isEditing && fieldKey ? (
                              <input
                                type="text"
                                value={editFormData.financials?.[fieldKey] || ''}
                                onChange={(e) => handleInputChange(fieldKey, e.target.value, true)}
                                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box', marginTop: '4px' }}
                              />
                            ) : (
                              <span className="dpm-modal-value">{value || 'N/A'}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Documents */}
                  {partnerDetails.documents && partnerDetails.documents.length > 0 && (
                    <div className="dpm-modal-section">
                      <h3>Documents ({partnerDetails.documents.length})</h3>
                      <div className="dpm-docs-grid">
                        {partnerDetails.documents.map((doc, idx) => (
                          <div key={idx} className="dpm-doc-card">
                            <div className="dpm-doc-header">
                              <span className="dpm-doc-type">
                                {doc.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                              {isEditing ? (
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer', color: '#1f2937' }}>
                                  <input
                                    type="checkbox"
                                    checked={editFormData.documents?.find(d => d.document_type === doc.document_type)?.is_verified || false}
                                    onChange={(e) => handleDocumentChange(doc.document_type, e.target.checked)}
                                    style={{ margin: 0, cursor: 'pointer' }}
                                  />
                                  Verified
                                </label>
                              ) : (
                                <span className={`dpm-doc-status ${doc.is_verified ? 'verified' : 'unverified'}`}>
                                  {doc.is_verified ? '✓ Verified' : '✗ Unverified'}
                                </span>
                              )}
                            </div>
                            <a href={doc.document_url} target="_blank" rel="noopener noreferrer" className="dpm-doc-link">
                              View Document
                            </a>
                            {isEditing && (
                              <div style={{ marginTop: '8px' }}>
                                <label style={{ display: 'inline-block', padding: '4px 8px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', cursor: uploadingDocs[doc.document_type] ? 'not-allowed' : 'pointer', color: '#475569' }}>
                                  {pendingUploads[doc.document_type] ? 'File Selected' : 'Replace Document'}
                                  <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleDocumentSelect(doc.document_type, e.target.files[0])}
                                  />
                                </label>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="dpm-modal-error">Failed to load partner details.</div>
              )}
            </div>

            {partnerDetails && !detailsLoading && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#f8fafc', borderRadius: '0 0 12px 12px' }}>
                <button onClick={() => { setShowDetailsModal(false); setPartnerDetails(null); setIsEditing(false); }} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', fontWeight: 500, color: '#475569' }}>Close</button>
                {isEditing ? (
                  <>
                    <button onClick={handleEditToggle} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', fontWeight: 500, color: '#475569' }}>Cancel</button>
                    <button onClick={handleSaveDetails} disabled={isSaving} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#F55D00', color: 'white', cursor: 'pointer', fontWeight: 500 }}>{isSaving ? 'Saving...' : 'Save Changes'}</button>
                  </>
                ) : (
                  <button onClick={handleEditToggle} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#F55D00', color: 'white', cursor: 'pointer', fontWeight: 500 }}>Edit Details</button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryPartnerManagement;
